import { PayloadAction, createSlice } from '@reduxjs/toolkit'

import type {
  CommunicationModel,
  CommunicationsRevisionIdentity,
} from './communication.model'

export const getCommunicationsRevisionKey = ({
  organizationId,
  personId,
}: CommunicationsRevisionIdentity) => `${organizationId}:${personId}`

const initialState: CommunicationModel = {
  newCommunication: false,
  newCommunicationIdentity: null,
  communicationsRevisions: {},
}

const communicationStore = createSlice({
  name: 'communication',
  initialState,
  reducers: {
    setNewCommunication: (state, action: PayloadAction<boolean>) => {
      state.newCommunication = action.payload
      state.newCommunicationIdentity = null
    },
    openNewCommunicationForIdentity: (state, action: PayloadAction<string>) => {
      state.newCommunication = true
      state.newCommunicationIdentity = action.payload
    },
    incrementCommunicationsRevision: (
      state,
      action: PayloadAction<CommunicationsRevisionIdentity>,
    ) => {
      const key = getCommunicationsRevisionKey(action.payload)
      state.communicationsRevisions[key] =
        (state.communicationsRevisions[key] ?? 0) + 1
    },
  },
})

export const {
  incrementCommunicationsRevision,
  openNewCommunicationForIdentity,
  setNewCommunication,
} = communicationStore.actions

export const selectCommunicationsRevision = (
  state: { communication: CommunicationModel },
  identity: CommunicationsRevisionIdentity,
) =>
  state.communication.communicationsRevisions[
    getCommunicationsRevisionKey(identity)
  ] ?? 0

export const incrementCommunicationsRevisionsOnSuccess = async (
  queryFulfilled: PromiseLike<unknown>,
  dispatch: (
    action: ReturnType<typeof incrementCommunicationsRevision>,
  ) => unknown,
  identities: CommunicationsRevisionIdentity[],
) => {
  try {
    await queryFulfilled
  } catch {
    return
  }

  const refreshedIdentities = new Set<string>()
  identities.forEach((identity) => {
    if (!identity.organizationId || !identity.personId) return

    const key = getCommunicationsRevisionKey(identity)
    if (refreshedIdentities.has(key)) return

    refreshedIdentities.add(key)
    dispatch(incrementCommunicationsRevision(identity))
  })
}

export default communicationStore.reducer
